import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BarChart2, RefreshCw, Download, ChevronDown, Loader2, ExternalLink } from 'lucide-react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { cn } from '../lib/utils';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import { getUserAccountId } from '../lib/access';
import imageILogo from '../../assets/images/image-i-logo.png';

// powerbi-client's WindowPostMessageProxy assumes every MessageEvent has a
// live source window and calls source.postMessage() without a null check.
// Chromium can deliver a late/null-source event while an embed iframe is
// being replaced or removed. Stop only those unusable events in capture phase
// so the SDK cannot turn a harmless lifecycle race into an uncaught error.
if (typeof window !== 'undefined' && !window.__imageiPowerBiMessageGuard) {
  window.addEventListener('message', (event) => {
    if (!event.source) event.stopImmediatePropagation();
  }, true);
  window.__imageiPowerBiMessageGuard = true;
}

// ─── Helpers (shared with DepartmentCockpit logic) ────────────────────────────
function parsePowerBIUrl(url) {
  if (!url) return { isSecure: false, pbReportId: null, groupId: null };
  try {
    const u = new URL(url);
    if (u.pathname.includes('/reportEmbed')) {
      return { isSecure: true, pbReportId: u.searchParams.get('reportId'), groupId: u.searchParams.get('groupId') };
    }
  } catch (_) {}
  return { isSecure: false, pbReportId: null, groupId: null };
}

function getReportEmbedUrl(r) {
  return (
    r.embedURL ||
    r.embedUrl ||
    r.embed_url ||
    r.powerbi_url ||
    r.powerbi_URL ||
    r.reportData ||
    r.report_data ||
    r.url ||
    null
  );
}

function augmentReport(r) {
  const embedUrl = getReportEmbedUrl(r);
  const { isSecure: urlSecure, pbReportId: urlPbId, groupId: urlGroupId } = parsePowerBIUrl(embedUrl);
  const dbSecure  = r.isSecure   != null ? r.isSecure   : r.is_secure;
  const dbGroupId = r.groupId    != null ? r.groupId    : r.group_id;
  const dbDataset = r.datasetId  != null ? r.datasetId  : r.dataset_id;
  const dbRlsRole = r.rlsRole    != null ? r.rlsRole    : r.rls_role;
  const dbPbId    = r.reportId   != null ? r.reportId   : r.report_id;
  return {
    ...r,
    isSecure:   dbSecure  != null ? Boolean(dbSecure)  : urlSecure,
    pbReportId: dbPbId    || urlPbId    || null,
    groupId:    dbGroupId || urlGroupId || null,
    datasetId:  dbDataset  || null,
    rlsRole:    dbRlsRole  || null,
    embedUrl,
  };
}

function hasEmbeddedReport(report) {
  return Boolean(
    getReportEmbedUrl(report) ||
    report?.pbReportId ||
    report?.reportId ||
    report?.report_id
  );
}

function toReportArray(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  return (
    data.data ||
    data.value ||
    data.reports ||
    data.allocated_reports ||
    data.allocatedReports ||
    data.results ||
    data.items ||
    []
  );
}

function sanitizeFilename(name) {
  return String(name || 'report')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'report';
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function flattenEmbeddedReports(reports) {
  const flat = [];

  const walk = (items, parent) => {
    (Array.isArray(items) ? items : []).forEach((report) => {
      const nextParent = parent || report.parent || null;
      if (report.children?.length) {
        walk(report.children, report.title || report.name || nextParent);
        return;
      }

      const augmented = augmentReport({ ...report, parent: nextParent });
      if (hasEmbeddedReport(augmented)) {
        flat.push(augmented);
      }
    });
  };

  walk(reports, null);
  return flat;
}

// ─── Report card selector ─────────────────────────────────────────────────────
function ReportCardList({ reports, onSelect }) {
  if (!reports.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <BarChart2 className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No allocated embedded reports found.</p>
        <p className="text-xs text-muted-foreground">Assign reports to this user or organization from Report Allocation.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {reports.map((r) => (
        <button
          key={r.id || r.reportId || r.report_id || r.title}
          type="button"
          onClick={() => onSelect(r)}
          className="surface-card rounded-xl p-4 text-left border border-border hover:border-primary/40 hover:bg-primary/5 transition group"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition">
              <BarChart2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
              {r.parent && <p className="text-xs text-muted-foreground mt-0.5">{r.parent}</p>}
              <div className="mt-2 flex items-center gap-1">
                <span className={cn(
                  'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                  r.isSecure
                    ? 'bg-success/10 text-success border border-success/20'
                    : 'bg-primary/10 text-primary border border-primary/20'
                )}>
                  {r.isSecure ? 'Secure embed' : 'Public'}
                </span>
              </div>
            </div>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary flex-shrink-0 transition" />
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Power BI embed viewer ────────────────────────────────────────────────────
function PowerBiViewer({ report, onBack }) {
  const [loaded,      setLoaded]      = useState(false);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [exportError, setExportError] = useState('');
  const [tokenState,  setTokenState]  = useState({ accessToken: null, sdkEmbedUrl: null, error: null });
  const reportRef = useRef(null);
  const captureRef = useRef(null);
  const account   = useSelector((state) => state.account);
  const org_id =
    account?.org?.id ||
    account?.org?.data?.id ||
    account?.org_data?.id ||
    account?.org_data?.data?.id ||
    account?.org_member?.org_id ||
    account?.UserDetails?.org_id ||
    account?.user?.org_id;
  const user_id = getUserAccountId(account);

  useEffect(() => () => {
    // Explicitly detach the SDK embed before React removes its iframe. This
    // prevents delayed SDK responses targeting an already-destroyed window.
    const embeddedReport = reportRef.current;
    const element = embeddedReport?.element;
    if (element && window.powerbi?.reset) {
      try { window.powerbi.reset(element); } catch (_) {}
    }
    reportRef.current = null;
  }, []);

  // Fetch embed token for secure reports
  useEffect(() => {
    setLoaded(false);
    setTokenState({ accessToken: null, sdkEmbedUrl: null, error: null });

    if (!report.isSecure) return; // public reports use iframe; nothing to fetch

    const fetchToken = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const snapshotToken = urlParams.get('snapshot_token');
        const localToken = localStorage.getItem('token')?.replace(/^"|"$/g, '');
        const tokenToUse = snapshotToken || localToken;

        const res = await axiosInstance.post(
          `${String(configData.API_SERVER || '').replace(/\/+$/, '')}/user/get-powerbi-embed-token`,
          {
            reportId:  report.pbReportId,
            datasetId: report.datasetId,
            groupId:   report.groupId,
            rlsRole:   report.rlsRole || null,
            orgId:     org_id,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              ...(tokenToUse ? { Authorization: `Bearer ${tokenToUse}` } : {}),
            },
          }
        );
        const { embedToken, embedUrl: tokenEmbedUrl, groupId: tGroupId } = res.data;
        const finalGroupId = tGroupId || report.groupId;

        // Construct proper SDK embed URL — the stored embedURL may be a public view?r=... link
        // which the Power BI SDK cannot use. Always prefer the SDK format.
        const sdkEmbedUrl = tokenEmbedUrl
          || (report.pbReportId && finalGroupId
              ? `https://app.powerbi.com/reportEmbed?reportId=${report.pbReportId}&groupId=${finalGroupId}`
              : report.embedUrl);

        setTokenState({ accessToken: embedToken, sdkEmbedUrl, error: null });
      } catch (err) {
        console.error('PowerBiViewer token fetch error:', err);
        setTokenState({ accessToken: null, sdkEmbedUrl: null, error: 'Failed to load report. Please try again.' });
      }
    };

    fetchToken();
  }, [report]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadReport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${configData.API_SERVER}user/download-report?report_id=${report.pbReportId}&format=${format}&org_id=${org_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${report.title || 'report'}.${format.toLowerCase()}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('Download error:', err);
    }
    setMenuOpen(false);
  };

  const handleRefresh = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${configData.API_SERVER}user/refresh_powerbi_dataset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ datasetId: report.datasetId, groupId: report.groupId }),
      });
      setTimeout(() => {
        reportRef.current?.refresh().catch(() => reportRef.current?.reload());
      }, 60000);
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  const exportReportSnapshotPdf = async () => {
    const snapshotReportId = report.pbReportId || report.reportId || report.report_id || report.id;
    if (!snapshotReportId || !org_id) {
      setExportError('Report ID or organization ID is missing.');
      return;
    }
    setExportError('');
    setExporting(true);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        report_id: String(snapshotReportId),
        org_id: String(org_id),
        title: report.title || report.name || 'IMAGE-I Report',
        embed_url: report.embedUrl || getReportEmbedUrl(report) || '',
        is_secure: report.isSecure ? '1' : '0',
      });

      if (user_id) params.set('user_id', String(user_id));
      if (report.pbReportId) params.set('pb_report_id', String(report.pbReportId));
      if (report.groupId) params.set('group_id', String(report.groupId));
      if (report.datasetId) params.set('dataset_id', String(report.datasetId));
      if (report.rlsRole) params.set('rls_role', String(report.rlsRole));

      const response = await fetch(
        `${configData.API_SERVER}user/download-report-snapshot/?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Snapshot export failed.');
      }
      const blob = await response.blob();
      downloadBlob(blob, `${sanitizeFilename(report.title)}.pdf`);
    } catch (err) {
      console.error('Report PDF export error:', err);
      setExportError('Snapshot export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-muted-foreground hover:text-foreground transition flex items-center gap-1"
          >
            ← All reports
          </button>
          <span className="text-muted-foreground">·</span>
          <span className="text-sm font-medium text-foreground">{report.title}</span>
          {report.isSecure && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 font-medium">
              Secure embed
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportReportSnapshotPdf}
            disabled={exporting || !loaded}
            className="h-8 px-3 rounded-lg border border-border flex items-center gap-1.5 text-xs font-medium hover:bg-muted transition disabled:opacity-60 disabled:cursor-not-allowed"
            title="Export current report screenshot as PDF"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? 'Exporting...' : 'Export PDF'}
          </button>

          {report.isSecure && (
            <>
            <button
              type="button"
              onClick={handleRefresh}
              className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition"
              title="Refresh dataset"
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="h-8 px-3 rounded-lg border border-border flex items-center gap-1.5 text-xs font-medium hover:bg-muted transition"
              >
                <Download className="h-3.5 w-3.5" />
                Export
                <ChevronDown className="h-3 w-3" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-9 z-20 surface-card rounded-lg border border-border shadow-lg py-1 min-w-[140px]">
                    {['PDF', 'PPTX', 'XLSX'].map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => downloadReport(fmt)}
                        className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition"
                      >
                        Download as {fmt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            </>
          )}
        </div>
      </div>

      {exportError && (
        <p className="text-xs text-destructive">{exportError}</p>
      )}

      {/* Embed area */}
      <div ref={captureRef} className="surface-card rounded-xl overflow-hidden relative" style={{ minHeight: 560 }}>

        {/* Loading / error overlay */}
        {(!loaded || (report.isSecure && !tokenState.accessToken && !tokenState.error)) && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card">
            {tokenState.error ? (
              <p className="text-sm text-destructive">{tokenState.error}</p>
            ) : (
              <>
                <img src={imageILogo} alt="IMAGE-I" style={{ width: 160 }} />
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
              </>
            )}
          </div>
        )}

        {report.isSecure ? (
          // Secure: use PowerBIEmbed component (handles SDK init internally — no window.powerbi needed)
          tokenState.accessToken && tokenState.sdkEmbedUrl ? (
            <PowerBIEmbed
              embedConfig={{
                type: 'report',
                id: report.pbReportId,
                embedUrl: tokenState.sdkEmbedUrl,
                accessToken: tokenState.accessToken,
                tokenType: models.TokenType.Embed,
                settings: {
                  layoutType: models.LayoutType.Custom,
                  customLayout: { displayOption: models.DisplayOption.FitToWidth },
                  panes: { filters: { visible: false } },
                  navContentPaneEnabled: false,
                },
              }}
              eventHandlers={new Map([
                ['loaded',   () => setLoaded(true)],
                ['rendered', () => setLoaded(true)],
                ['error',    (event) => { console.error('PowerBI embed error', event?.detail); setLoaded(true); }],
              ])}
              cssClassName="ih-powerbi-embed"
              style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
              getEmbeddedComponent={(embeddedReport) => { reportRef.current = embeddedReport; }}
            />
          ) : null
        ) : (
          // Public: plain iframe
          <div className="relative" style={{ paddingTop: '56.25%' }}>
            <iframe
              title={report.title}
              src={report.embedUrl || getReportEmbedUrl(report)}
              onLoad={() => setLoaded(true)}
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%',
                border: 'none',
                opacity: loaded ? 1 : 0,
                transition: 'opacity 0.3s',
              }}
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main ReportEmbed component ───────────────────────────────────────────────
export function ReportEmbed({ title, subtitle, className, category, autoOpenFirst = false }) {
  const account = useSelector((state) => state.account);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setSelected(null);
        setAutoOpened(false);
        const getAllocatedReports = async (query) => {
          try {
            const res = await axiosInstance.get(`${configData.API_SERVER}user/allocated-reports?${query}`);
            return toReportArray(res?.data);
          } catch (err) {
            const message = err?.response?.data?.error || err?.response?.data?.message || '';
            if (err?.response?.status === 404 || /No allocated reports found/i.test(message)) {
              return [];
            }
            throw err;
          }
        };
        const currentUserId = getUserAccountId(account);
        const orgMemberId = account?.org_member?.id || null;

        // Use the same allocation identity path as Overview and the department
        // cockpits. The backend resolves OrgMember -> UserAccount by email,
        // returns direct user allocations first, and preserves legacy
        // organisation allocations when no direct rows exist.
        const userReports = orgMemberId
          ? await getAllocatedReports(`org_member_id=${encodeURIComponent(orgMemberId)}`)
          : currentUserId
            ? await getAllocatedReports(`user_id=${encodeURIComponent(currentUserId)}`)
            : [];

        // Flatten nested report groups and keep only actual embeddable reports.
        const flat = flattenEmbeddedReports(userReports);

        // Filter by category if provided
        const filtered = category
          ? flat.filter((r) =>
              (r.parent && r.parent.toLowerCase() === category.toLowerCase()) ||
              (r.category && r.category.toLowerCase() === category.toLowerCase()) ||
              (r.title && r.title.toLowerCase().startsWith(category.toLowerCase()))
            )
          : flat;

        setReports(filtered);
      } catch (err) {
        console.error('Error fetching reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoOpenFirst && !autoOpened && !selected && reports.length > 0) {
      setSelected(reports[0]);
      setAutoOpened(true);
    }
  }, [autoOpenFirst, autoOpened, reports, selected]);

  return (
    <div className={cn('surface-card rounded-xl p-5', className)}>
      {/* Header */}
      {!selected && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground">
            {title || (category ? `${category} Reports` : 'Reports')}
          </h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : selected ? (
        <PowerBiViewer report={selected} onBack={() => setSelected(null)} />
      ) : (
        <ReportCardList reports={reports} onSelect={setSelected} />
      )}
    </div>
  );
}
